import { PartnerGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import {
  NotificationChannelType,
  NotificationEvent,
} from 'src/graphql/entities/notification/notification.types';

export type NotificationCategoryConfig = {
  displayName: string;
  icon: string;
  partnerGroupRules?: PartnerGroupRules[];
};

const notificationCategoryToConfig = new Map<string, NotificationCategoryConfig>([
  ['conversation', { displayName: 'conversations', icon: 'message-circle' }],
  ['signal', { displayName: 'Signal', icon: 'zap' }],
  ['crm', { displayName: 'CRM', icon: 'user' }],
  [
    'project',
    {
      displayName: 'Projects',
      icon: 'image',
      partnerGroupRules: [PartnerGroupRules.AccessToProvisionProjects],
    },
  ],
  [
    'subscription',
    {
      displayName: 'Subscriptions',
      icon: 'credit-card',
      partnerGroupRules: [PartnerGroupRules.AccessToSubscriptionManagement],
    },
  ],
]);

export const getCategoryConfig = (category: string): NotificationCategoryConfig => {
  return (
    notificationCategoryToConfig.get(category) ?? {
      displayName: category,
      icon: 'bell',
    }
  );
};

/**
 * @param categoryConfig The category config
 * @param partnerGroupRules The set of partner group rule values for the current user
 * @returns True if the user has access to one of the category's required partner group rules, false otherwise.
 */
export const shouldShowCategory = (
  categoryConfig: NotificationCategoryConfig,
  partnerGroupRules: Record<PartnerGroupRules, boolean>,
): boolean => {
  if (!categoryConfig.partnerGroupRules) {
    return true;
  }
  return categoryConfig.partnerGroupRules.some((rule) => partnerGroupRules[rule] === true);
};

export type NotificationChannelConfig = {
  icon: string;
};

export const notificationChannelToConfigMap = new Map<string, NotificationChannelConfig>([
  ['email', { icon: 'mail' }],
  ['in_app', { icon: 'bell' }],
  ['push', { icon: 'bell-ring' }],
]);

const EMAIL_ONLY_EVENTS = new Set<string>([NotificationEvent.WeeklySiteDigest]);
const EMAIL_CHANNEL_ID: string = NotificationChannelType.Email;

export const MAX_WEEKLY_DIGEST_EXCLUDED_SITES = 200;

export function isWeeklyDigestEvent(eventId: string): boolean {
  return EMAIL_ONLY_EVENTS.has(eventId);
}

export function skippedSitesLabel(count: number): string {
  return count === 1 ? '1 site skipped' : `${count} sites skipped`;
}

export function excludedAddressIdsFromPreference(config: string | null | undefined): string[] {
  if (!config) return [];
  try {
    const addressIds = (JSON.parse(config) as { exclude?: { addressIds?: unknown } }).exclude
      ?.addressIds;
    if (!Array.isArray(addressIds)) return [];
    return addressIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
  } catch {
    return [];
  }
}

export function weeklyDigestExclusionConfig(addressIds: string[]): string | null {
  const ids = [...new Set(addressIds)].sort();
  return ids.length ? JSON.stringify({ exclude: { addressIds: ids } }) : null;
}

export function channelsForNotificationEvent<T extends { id: string }>(
  eventId: string,
  channels: T[],
): T[] {
  if (!EMAIL_ONLY_EVENTS.has(eventId)) {
    return channels;
  }
  return channels.filter((channel) => channel.id === EMAIL_CHANNEL_ID);
}

export function rolesPreselectedForEvent<T>(event: {
  roles?: T[] | null;
  defaultRoles?: T[] | null;
}): T[] {
  return event.defaultRoles?.length ? event.defaultRoles : (event.roles ?? []);
}
